// istanbul ignore file
import _ from 'lodash';
import { createSelector } from 'reselect';
import {
  getDischargeModuleForCurrentLocale,
  getCurrentModule,
  getLearnModulesForCurrentLocale
} from './modules';
import {
  getLessonsWithOrderedPages,
  getLessonIdToPageCountMap,
  getDischargeLessons
} from './lessons';
import {
  getProgressObjects, 
  getLessons,
  getLessonPages,
  getModuleIdToLessonPageIdsMap,
  getDischargeProgressObjects,
  getCurrentLessonSlug
} from './core';

const EMPTY_ARRAY = [];

const getCompletedLessonPageIds = createSelector(
  getProgressObjects,
  progress => _.map(progress, p => p.lessonPageId)
);

const getStartedLessonIds = createSelector(
  getDischargeProgressObjects,
  progress => _.map(progress, p => p.lessonId)
);

const getLessonIdToMostRecentlyCompletedPageNumberMap = createSelector(
  getLessonsWithOrderedPages,
  getCompletedLessonPageIds,
  (lessons, completedPageIds) =>
    _.mapValues(lessons, lesson =>
      _.reduce(
        lesson.pages,
        (res, page) =>
          _.includes(completedPageIds, page.id) && page.displayOrder > res
            ? page.displayOrder
            : res,
        0
      )
    )
);

const getLessonIdToNextPageNumberMap = createSelector(
  getLessons,
  getLessonIdToMostRecentlyCompletedPageNumberMap,
  getLessonIdToPageCountMap,
  (
    lessons,
    lessonIdToMostRecentlyCompletedPageNumberMap,
    lessonIdToPageCountMap
  ) =>
    _.mapValues(lessons, lesson => {
      const mostRecentlyCompletedPageNum =
        lessonIdToMostRecentlyCompletedPageNumberMap[lesson.id];
      const totalPagesInLesson = lessonIdToPageCountMap[lesson.id];

      if (mostRecentlyCompletedPageNum === totalPagesInLesson) {
        return 1;
      }
      return mostRecentlyCompletedPageNum + 1;
    })
);

const getLessonPageIdToIsReadMap = createSelector(
  getLessonPages,
  getCompletedLessonPageIds,
  (lessonPages, completedPageIds) =>
    _.mapValues(lessonPages, lessonPage =>
      _.includes(completedPageIds, lessonPage.id)
    )
);

const getLessonProgressForCurrentProfile = createSelector(
  getLessons,
  getStartedLessonIds,
  getCompletedLessonPageIds,
  (lessons, startedLessonIds, completedLessonPageIds) =>
    _.reduce(
      lessons,
      (result, lesson) => {
        const allPageIdsForLesson = lesson.pages || [];
        const numCompleted = _.intersection(allPageIdsForLesson, completedLessonPageIds).length;
        const numTotal = allPageIdsForLesson.length;

        let lessonProgress = 0;
        if (numCompleted > 0 && numTotal > 0) {
          lessonProgress = (numCompleted / numTotal) * 100;
        } else if (_.includes(startedLessonIds, lesson.id)) {
          lessonProgress = 1;
        }

        return { ...result, [lesson.id]: lessonProgress };
      },
      {}
    )
);

const getModuleProgressForCurrentProfile = createSelector(
  getModuleIdToLessonPageIdsMap,
  getCompletedLessonPageIds,
  (moduleIdToLessonPageIdsMap, completedLessonPageIds) =>
    _.reduce(
      moduleIdToLessonPageIdsMap,
      (result, lessonPageIds, moduleId) => {
        const numCompleted = _.intersection(lessonPageIds, completedLessonPageIds).length;
        const numTotal = lessonPageIds.length;
        const progress = numTotal === 0 ? 0 : (numCompleted / numTotal) * 100;
        return { ...result, [moduleId]: progress };
      },
      {}
    )
);

const getDischargeLessonStartOrder = createSelector(
  getDischargeLessons,
  getProgressObjects,
  getDischargeProgressObjects,
  (lessons, progressObjects, dischargeProgressObjects) => {
    const sortedProgress = _.sortBy(progressObjects, 'createdAt');
    const sortedDischargeProgress = _.sortBy(dischargeProgressObjects, 'createdAt');

    return _.chain(lessons)
      .map(lesson => {
        let firstProgress = _.find(sortedDischargeProgress, p => lesson.id === p.lessonId);
        if (!firstProgress) {
          firstProgress = _.find(sortedProgress, p => _.includes(lesson.pages, p.lessonPageId));
        }
        return {
          lessonId: lesson.id,
          startDate: firstProgress ? firstProgress.createdAt : null
        };
      })
      .sortBy('startDate')
      .value();
  }
);

const getDischargeProgressForCurrentProfile = createSelector(
  getDischargeModuleForCurrentLocale,
  getModuleProgressForCurrentProfile,
  getStartedLessonIds,
  (dischargeModule, moduleProgress, startedLessonIds) =>
    startedLessonIds.length ? 1 : moduleProgress[dischargeModule?.id] || 0
);

const getCurrentProgress = createSelector(
  getProgressObjects,
  progress => progress.map(p => ({
    ...p,
    topics: p.topics && Array.isArray(p.topics) ? p.topics : []
  }))
);

const buildCompletedIdSet = (progress) => {
  const ids = new Set();
  for (const p of progress) {
    if (p.topicId) ids.add(String(p.topicId));
    if (p.lessonPageId) ids.add(String(p.lessonPageId));
    if (Array.isArray(p.topics)) {
      for (const t of p.topics) {
        if (!t) continue;
        const id = typeof t === 'object' ? (t.topicId || t.topic_id || t.ID || t.id) : t;
        if (id) ids.add(String(id));
      }
    }
  }
  return ids;
};

const mapModuleToProgression = (module, progress) => {
  if (!module) return module;

  const moduleName = module.acf?.title || module.title?.rendered || module.slug || `Module ID: ${module.ID}`;
  const isReadingResource = moduleName.includes("Feeding") || moduleName.includes("NICU") || moduleName.includes("Gestational");

  if (!module.lessons) {
      return { ...module, completion: 0 };
  }

  // Pre-build a flat Set of all completed IDs so per-topic lookup is O(1)
  const completedIdSet = buildCompletedIdSet(progress);

  const lessons = module.lessons.map(lesson => {
    const completedTopics = [];
    
    const topics = (lesson.topics || []).map(topic => {
      const currentTopicId = String(topic.ID || topic.id || topic.post_id);
      const completed = completedIdSet.has(currentTopicId);

      if (completed) completedTopics.push(currentTopicId);
      return { ...topic, completed };
    });

    const lessonCompletion = topics.length > 0 ? (completedTopics.length / topics.length) * 100 : 0;

    return {
      ...lesson,
      topics,
      completion: lessonCompletion
    };
  });

  const weight = (lessons.length || 1) * 100;
  const completionModule = lessons.reduce((total, lesson) => total + (lesson.completion / weight) * 100, 0);

  return {
    ...module,
    lessons,
    completion: Math.round(completionModule)
  };
};

const getCurrentModuleWithLessonsAndProgress = createSelector(
  getCurrentModule,
  getCurrentProgress,
  (module, progress) => module ? mapModuleToProgression(module, progress) : undefined
);

const getCurrentLessonWithProgress = createSelector(
  getCurrentModuleWithLessonsAndProgress,
  getCurrentLessonSlug,
  (currentModule, lessonSlug) => {
    if (currentModule?.lessons) {
      return currentModule.lessons[parseInt(lessonSlug, 10) - 1];
    }
    return null;
  }
);

const getAllModulesWithProgress = createSelector(
  getLearnModulesForCurrentLocale,
  getCurrentProgress,
  (modules, progress) => {
    if (!modules || !Array.isArray(modules)) return EMPTY_ARRAY;
    return modules.map(module => mapModuleToProgression(module, progress));
  }
);

export {
  buildCompletedIdSet,
  getProgressObjects,
  getCompletedLessonPageIds,
  getModuleProgressForCurrentProfile,
  getLessonProgressForCurrentProfile,
  getLessonIdToNextPageNumberMap,
  getLessonPageIdToIsReadMap,
  getDischargeLessonStartOrder,
  getDischargeProgressForCurrentProfile,
  getCurrentModuleWithLessonsAndProgress,
  getCurrentProgress,
  getAllModulesWithProgress,
  getCurrentLessonWithProgress
};
